import React, { useEffect, useRef, useState } from "react";
import { getGallery } from "../../redux/thunks/serviceRegThunk";
import { useDispatch, useSelector } from "react-redux";
import { X } from "lucide-react";
import SkeletonGrid from "../Preloader/SkeletonGrid";

const ITEMS_PER_LOAD = 12;

const PhotoLibraryModal = ({
  onClose,
  onToggleSelect,
  selectedImages = [],
}) => {
  const dispatch = useDispatch();
  const observerRef = useRef(null);

  const { galleries, isServiceLoading } = useSelector(
    (state) => state.setService,
  );

  const allImages = galleries?.sPImageGalleryData || [];
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);

  useEffect(() => {
    dispatch(getGallery());
  }, [dispatch]);

  useEffect(() => {
    if (!observerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + ITEMS_PER_LOAD, allImages.length),
          );
        }
      },
      { threshold: 1 },
    );

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [allImages.length]);

  const visibleImages = allImages.slice(0, visibleCount);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm
      flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* MODAL */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-7xl rounded-3xl
        shadow-2xl overflow-hidden animate-scaleIn"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Photo Library
            </h2>
            <p className="text-sm text-gray-500">
              Select an image from your gallery
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {isServiceLoading ? (
            <SkeletonGrid count={12} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {visibleImages.map((item) => {
                const isSelected = selectedImages.includes(item.id);

                return (
                  <button
                    key={item.id}
                    onClick={() => onToggleSelect(item)}
                    className={`
        group relative rounded-2xl overflow-hidden
        border-2 transition-all
        ${
          isSelected
            ? "border-[#FF6943]"
            : "border-gray-200 hover:border-[#FF6943]"
        }
      `}
                  >
                    <img
                      src={item.image}
                      alt=""
                      loading="lazy"
                      className="w-full h-36 object-cover"
                    />

                    {/* Dark overlay when selected */}
                    {isSelected && (
                      <div className="absolute inset-0 bg-black/40" />
                    )}

                    {/* Tick mark */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-[#FF6943] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                        ✓
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* OBSERVER */}
          {!isServiceLoading && visibleCount < allImages.length && (
            <div ref={observerRef} className="h-10 mt-6" />
          )}
        </div>

        {/* FOOTER */}
        <div className="flex justify-between gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#FF6943] text-white rounded-lg"
          >
            Done
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg border text-sm
            hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoLibraryModal;
